/**
 * Kestner's three seconds (`docs/beat-8-plan.md`, S-6).
 *
 * *"After the power is cut he comes down the alley with a torch. He crouches at
 * the box. He looks at it — at her height. Then he looks at the floor, and then
 * along the floor, the way she went. He does not find her. He straightens up and
 * writes something down."*
 *
 * ── He does not crouch, and this is what replaced it ────────────────────────
 *
 * `findKestnerParts` returns exactly one live pivot: the neck. `kestner.ts` says
 * why — `collapseRigged` took him from 44 draw calls to 32 by merging everything
 * under every pivot but that one, and the hips came off in that trade. He
 * therefore cannot bend, and lowering the whole figure to fake it makes a rigid
 * man sink into the floor.
 *
 * **So the torch crouches instead of the man.** The beam drops to the fuse box at
 * 0.30 m, holds there, drops to the floor, and travels away down the alley. The
 * plan's sentence is *"he looks at it — at her height"*, and in a hall that this
 * beat has just switched off, what the player is watching is not his knees. It is
 * the pool of light. Told with the light, the line survives intact and costs no
 * rig work; told with his body, it needed a pivot that was sold for a third of
 * his draw calls.
 *
 * ── One light, and it is at the target rather than in his hand ──────────────
 *
 * `torch.ts` explains why there is no beam geometry. What is left is a single
 * `PointLight`, and it is parked **on what he is looking at**, a little above the
 * surface — not at the lens. A light at the lens throws a sphere around Kestner
 * and lights nothing four metres away; a light at the target is a pool that
 * slides down the wall, onto the box, onto the floor, and off toward the door she
 * left by. That slide is the entire performance.
 *
 * It also leaves him a silhouette lit from in front and below by his own torch,
 * which is the best image this room has had.
 *
 * ── The torch does not add a light. It IS one of the hall's own ────────────
 *
 * `BlackoutHallDirector` hands in a fitting — a high bay the fuse has just taken
 * to zero — and this class drives its position, colour and reach. Nothing is
 * created and nothing is hidden, so the room renders **five visible point lights
 * from the first frame of the beat to the last.**
 *
 * That is not tidiness, it is the whole reason this works. D-027 found this GPU
 * bound by lights per fragment, and `numPointLights` is a **program cache key**:
 * every material in the room recompiles when the count moves. The first build of
 * this beat added a sixth light and hid four dead ones to pay for it, and the
 * budget gate read 21 compiled programs against a declared 14 — a recompile
 * storm landing on the exact frame the player throws the fuse, which is the most
 * dramatic moment in the beat and the worst possible place for a hitch. The gate
 * caught it; the comment it caught it with had predicted this exact failure.
 *
 * A `SpotLight` fails the same test twice over: a new light *type* is a new
 * permutation across every material, and it could never be an existing fitting.
 *
 * So the room does not lose four lights and gain one. It loses four lights, and
 * **one of them walks back in, carried.**
 */

import * as THREE from 'three';
import { getAsset } from '@/models/registry';
import { collapse } from '@/world/instancing';
import { findKestnerParts, type KestnerParts } from '@/models/env/kestner';
import { BLACKOUT_FUSE_INTERACT_AT, FAT_HALL } from '@/world/fattening';

/** In and out through the far cross-passage, past the pen run's ±10.8. */
const DOOR_Z = 11.6;
/** He walks the wall side of the alley: the crates are stacked on the other one. */
const WALK_X = 0.55;
/** Level with the fuse box and a little short of it, so the box is ahead and to his side. */
const STAND_Z = BLACKOUT_FUSE_INTERACT_AT.z + 0.8;
const WALK_SPEED = 1.2;

/** Where the torch hangs off him. No hand pivot exists; this is the hand. */
const HAND_AT = { x: -0.16, y: 1.12, z: 0.24 } as const;

/** `yardDirector.ts`'s numbers, and the same man, so they are imported by value not by copy. */
const HEAD_AT_BOARD = 0.34;
const HEAD_WALKING = 0.12;
const HEAD_UP = -0.16;

/** How bright a stock torch is against one distant emergency lamp. */
const TORCH_INTENSITY = 7.5;
const TORCH_RANGE = 4.2;
/** Tungsten, and warmer than the sodium fitting this light was ten seconds ago. */
const TORCH_COLOUR = 0xfff0cf;

/**
 * How close the sweep has to come to her before he reacts.
 *
 * A radius, not a cone: the light IS a point light, so a cone test here would be
 * a detection volume that does not match a single pixel of what the player sees.
 * D-100's rule about gates measuring the staged thing applies to fairness too.
 */
const NOTICE_RADIUS = 1.5;

type Phase = 'approach' | 'to-box' | 'hold-box' | 'to-floor' | 'sweep' | 'write' | 'leave' | 'gone';

/** Seconds each phase lasts. `approach` and `leave` are distances, not durations. */
const HOLD_BOX_FOR = 2.2;
const TO_BOX_FOR = 0.8;
const TO_FLOOR_FOR = 1.0;
const SWEEP_FOR = 3.0;
const WRITE_FOR = 1.6;
/** What being seen costs, and it is time rather than a fail state. See `noticed`. */
const NOTICE_HOLD = 1.6;

export class BlackoutTorch {
  readonly #figure: THREE.Object3D;
  readonly #parts: KestnerParts;
  readonly #hand: THREE.Object3D;
  readonly #light: THREE.PointLight;
  /** Where the beam is pointing now, eased toward `#want` every frame. */
  readonly #aim = new THREE.Vector3();
  readonly #want = new THREE.Vector3();

  #phase: Phase = 'approach';
  #elapsed = 0;
  #z = DOOR_Z;
  #noticed = false;
  #noticeHeld = 0;

  /** Everything about the borrowed fitting that has to go back. See `dispose`. */
  readonly #lightWas: { intensity: number; distance: number; decay: number; colour: number; x: number; y: number; z: number };

  constructor(scene: THREE.Object3D, light: THREE.PointLight) {
    const kestner = getAsset('kestner');
    if (!kestner) throw new Error('blackout torch: kestner asset is not registered');
    const torch = getAsset('torch');
    if (!torch) throw new Error('blackout torch: torch asset is not registered');

    this.#figure = kestner.create();
    this.#figure.name = 'blackout-kestner';
    this.#figure.position.set(WALK_X, 0, DOOR_Z);
    // Facing −Z, which is down the hall toward the door she came in by. `rotation.y`
    // of π rather than a negative angle so the sign matches `KESTNER_AT.facing`.
    this.#figure.rotation.y = Math.PI;
    this.#figure.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh) mesh.castShadow = true;
    });
    this.#parts = findKestnerParts(this.#figure);

    this.#hand = new THREE.Object3D();
    this.#hand.name = 'blackout-kestner-hand';
    this.#hand.position.set(HAND_AT.x, HAND_AT.y, HAND_AT.z);
    const prop = torch.create();
    prop.name = 'blackout-torch';
    // Seven meshes in three surfaces, and nothing on it moves. Unmerged that is
    // ~21 draw calls for a prop that is 170 mm long and mostly seen as a
    // silhouette behind its own light — measured at 88 for the whole staged
    // delta before this line existed. `kestner.ts` pays the same tax knowingly
    // for a rig; a torch has no pivot to protect.
    collapse(prop, 'blackout-torch-merged');
    this.#hand.add(prop);
    this.#figure.add(this.#hand);

    // Borrowed, not built. See the header for why the count must not move.
    this.#light = light;
    this.#lightWas = {
      intensity: light.intensity,
      distance: light.distance,
      decay: light.decay,
      colour: light.color.getHex(),
      x: light.position.x,
      y: light.position.y,
      z: light.position.z,
    };
    light.intensity = TORCH_INTENSITY;
    light.distance = TORCH_RANGE;
    light.decay = 2;
    light.color.setHex(TORCH_COLOUR);
    // The fitting keeps its own name in the graph. Renaming it would hide the one
    // fact worth being able to read there: this is a hall lamp, being carried.
    scene.add(this.#figure);

    this.#aim.set(WALK_X, 0, DOOR_Z - 3);
    this.#want.copy(this.#aim);
    this.#writeLight();
  }

  /** He is finished and back through the door. The stage waits on this and nothing else. */
  get finished(): boolean {
    return this.#phase === 'gone';
  }

  /**
   * The torch came within `NOTICE_RADIUS` of her while she was on the alley.
   *
   * **This is not a fail state and there is no capture.** Spec §7.8 has no
   * consequence here, `docs/beat-8-plan.md` says *"he does not find her"* and
   * *"no new detection channel"*, and beat 8 is the first beat of an act. What
   * being seen costs is `NOTICE_HOLD` seconds of him standing still with his head
   * up, looking at the place she is standing in, and then moving on. The gate
   * asserts the delta; the player gets the worst sixteen hundred milliseconds in
   * the beat and keeps everything they had.
   */
  get noticed(): boolean {
    return this.#noticed;
  }

  update(frameDt: number, at: { x: number; y: number; z: number }): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    if (dt === 0 || this.#phase === 'gone') return;
    this.#elapsed += dt;

    let head = HEAD_WALKING;

    switch (this.#phase) {
      case 'approach': {
        this.#z = Math.max(STAND_Z, this.#z - WALK_SPEED * dt);
        // Three metres ahead of his own boots, which is where anybody walking a
        // dark building points a torch.
        this.#want.set(WALK_X, 0, this.#z - 3);
        if (this.#z <= STAND_Z) this.#to('to-box');
        break;
      }
      case 'to-box': {
        this.#want.set(BLACKOUT_FUSE_INTERACT_AT.x, BLACKOUT_FUSE_INTERACT_AT.y, BLACKOUT_FUSE_INTERACT_AT.z);
        if (this.#elapsed >= TO_BOX_FOR) this.#to('hold-box');
        break;
      }
      case 'hold-box': {
        // The three seconds. Nothing moves, including him.
        this.#want.set(BLACKOUT_FUSE_INTERACT_AT.x, BLACKOUT_FUSE_INTERACT_AT.y, BLACKOUT_FUSE_INTERACT_AT.z);
        head = HEAD_UP;
        if (this.#elapsed >= HOLD_BOX_FOR) this.#to('to-floor');
        break;
      }
      case 'to-floor': {
        this.#want.set(WALK_X, 0, STAND_Z - 0.6);
        head = HEAD_UP;
        if (this.#elapsed >= TO_FLOOR_FOR) this.#to('sweep');
        break;
      }
      case 'sweep': {
        // Along the floor, the way she went — which is −Z, the door she used in
        // both directions. The travel is linear on purpose: a torch swept by a
        // tired man does not ease.
        const travel = Math.min(1, this.#elapsed / SWEEP_FOR);
        const from = STAND_Z - 0.6;
        this.#want.set(WALK_X, 0, from + (-1.5 - from) * travel);
        head = this.#noticeHeld > 0 ? HEAD_UP : HEAD_WALKING;

        const onTheAlley = Math.abs(at.x) <= FAT_HALL.alley;
        const dx = at.x - this.#want.x;
        const dz = at.z - this.#want.z;
        // ── `!this.#noticed` is the whole of this beat's softlock fix ─────────
        //
        // Without it he re-notices her on every frame she stays in range, and the
        // hold below freezes the sweep at exactly the point where she is — so the
        // pool never moves off her, the condition is true again next frame, and
        // **he never leaves.** The `torch` stage waits on him leaving, so that is
        // a hang in the first beat of Act 2, which is a save the player cannot get
        // out of. It is a real softlock, it was in the first build of this file,
        // and `blackout-hall.spec.ts` found it by waiting ninety seconds for a man
        // who was standing still.
        //
        // He gets one hold. He looks at the place she is standing in, he does not
        // find her, and he carries on — which is also the line the plan wrote.
        if (!this.#noticed && onTheAlley && dx * dx + dz * dz <= NOTICE_RADIUS * NOTICE_RADIUS) {
          this.#noticed = true;
          this.#noticeHeld = NOTICE_HOLD;
        }
        if (this.#noticeHeld > 0) {
          this.#noticeHeld -= dt;
          // The sweep stops while he holds, which is why the clock is wound back
          // rather than the phase being re-entered: re-entering would restart the
          // travel and walk the pool back up the hall.
          this.#elapsed -= dt;
          break;
        }
        if (this.#elapsed >= SWEEP_FOR) this.#to('write');
        break;
      }
      case 'write': {
        // Back onto the board in his hands. He is frightening because he writes
        // things down (`chapterCard.ts`), and this is the first time the player
        // watches him do it about her.
        this.#want.set(this.#figure.position.x - 0.2, 0.95, this.#z - 0.45);
        head = HEAD_AT_BOARD;
        if (this.#elapsed >= WRITE_FOR) this.#to('leave');
        break;
      }
      case 'leave': {
        this.#z = Math.min(DOOR_Z, this.#z + WALK_SPEED * dt);
        this.#want.set(WALK_X, 0, this.#z + 3);
        this.#figure.rotation.y = 0;
        if (this.#z >= DOOR_Z) {
          this.#phase = 'gone';
          // Back to nothing, NOT invisible: he takes the light out of the room
          // with him, and the fitting goes back to being a dead high bay. Hiding
          // it would move the count and undo the whole argument above.
          this.#light.intensity = 0;
          this.#figure.visible = false;
        }
        break;
      }
    }

    this.#figure.position.z = this.#z;
    // Eased toward the wanted point rather than snapped to it: the phases above
    // state where the beam should be, and this is the wrist that gets it there.
    this.#aim.lerp(this.#want, 1 - Math.exp(-7 * dt));
    this.#parts.neck.rotation.x += (head - this.#parts.neck.rotation.x) * (1 - Math.exp(-6 * dt));
    this.#writeLight();
  }

  #to(phase: Phase): void {
    this.#phase = phase;
    this.#elapsed = 0;
  }

  #writeLight(): void {
    // A quarter of a metre off whatever it is pointing at, so a pool on the floor
    // is a pool and not a hot dot burning through the slats.
    this.#light.position.set(this.#aim.x, this.#aim.y + 0.25, this.#aim.z);
    this.#figure.updateMatrixWorld(true);
    this.#hand.lookAt(this.#aim);
  }

  dispose(): void {
    // The fitting is the room's, and beat 8 is not the last thing that boots it.
    const was = this.#lightWas;
    this.#light.intensity = was.intensity;
    this.#light.distance = was.distance;
    this.#light.decay = was.decay;
    this.#light.color.setHex(was.colour);
    this.#light.position.set(was.x, was.y, was.z);
    this.#figure.removeFromParent();
  }
}
